import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Volume2Icon } from "lucide-react";
import { notFoundPhrases } from "../../data/notFound";
import { BrutalButton } from "../../components/ui/BrutalButton";
import { LangChip } from "../../components/ui/LangChip";
import { getLanguage } from "../../utils/language";
import { speak } from "../../utils/speech";
import { spring } from "../../utils/motion";
const tiles = [{
  char: "4",
  bg: "bg-peach",
  rotate: -7
}, {
  char: "0",
  bg: "bg-brand-tint",
  rotate: 3
}, {
  char: "4",
  bg: "bg-mint",
  rotate: 8
}];
export function NotFound() {
  const {
    pathname
  } = useLocation();
  const reduce = useReducedMotion() ?? false;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const phrase = notFoundPhrases[i];
  useEffect(() => {
    if (paused || reduce) return;
    const t = window.setInterval(() => setI(n => (n + 1) % notFoundPhrases.length), 2200);
    return () => window.clearInterval(t);
  }, [paused, reduce]);
  const hear = () => {
    speak(phrase.text, getLanguage(phrase.code).speech);
    setI(n => (n + 1) % notFoundPhrases.length);
  };
  return <section className="mx-auto flex max-w-3xl flex-col items-center px-5 py-16 text-center md:py-24" aria-labelledby="nf-title" data-id="element-1854" data-mp="17kpjrxekjifm">
      {/* Draggable sticker tiles — the 0 is a little globe */}
      <div className="flex items-end justify-center gap-3 md:gap-5" aria-hidden="true" data-id="element-1855" data-mp="ewa91n2gzma3">
        {tiles.map((t, k) => <motion.div key={k} drag={!reduce} dragSnapToOrigin dragElastic={0.5} initial={reduce ? false : {
        opacity: 0,
        y: 24,
        rotate: 0
      }} animate={{
        opacity: 1,
        y: 0,
        rotate: t.rotate
      }} whileHover={reduce ? undefined : {
        rotate: 0,
        y: -6
      }} whileDrag={{
        scale: 1.06,
        rotate: t.rotate * -1
      }} transition={{
        ...spring.bouncy,
        delay: reduce ? 0 : k * 0.05
      }} className={`grid h-28 w-24 cursor-grab touch-none select-none place-items-center rounded-panel border-3 border-line text-[76px] font-bold leading-none text-ink shadow-brutal active:cursor-grabbing md:h-44 md:w-36 md:text-[120px] ${t.bg}`} data-id="element-1856" data-mp="os3rto6s8c0b" data-mpi={k}>
            {t.char === "0" ? <Globe data-id="element-1857" data-mp="2h8ef22cohtgg" /> : t.char}
          </motion.div>)}
      </div>

      <h1 id="nf-title" className="mt-12 text-[clamp(38px,6vw,64px)] font-bold leading-[1.02] tracking-[-0.03em] text-ink" data-id="element-1858" data-mp="1jlxm19aza20i">
        Lost in translation.
      </h1>

      <button type="button" onClick={hear} onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)} aria-label={`Hear “${phrase.text}” in ${getLanguage(phrase.code).name}`} className="focus-ring group mt-5 flex h-12 items-center gap-3 rounded-xl border-3 border-line bg-surface px-3.5 shadow-brutal-sm transition-colors duration-150 hover:bg-sunken" data-id="element-1859" data-mp="1tjc0529fiepn">
        <LangChip code={phrase.code} size="sm" tone="tint" data-id="element-1860" data-mp="tb5i7xmijxl1" />
        <span className="relative block min-w-[13ch] overflow-hidden text-left" data-id="element-1861" data-mp="tbx4uiy4ukz6">
          <AnimatePresence mode="popLayout" initial={false} data-id="element-1862" data-mp="oar9edd38jly">
            <motion.span key={phrase.code} initial={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -14
          }} transition={{
            duration: 0.22,
            ease: [0.23, 1, 0.32, 1]
          }} className="block whitespace-nowrap text-[17px] font-semibold text-ink" lang={phrase.code} data-id="element-1863" data-mp="jd7v5dge46u0">
              {phrase.text}
            </motion.span>
          </AnimatePresence>
        </span>
        <Volume2Icon className="h-4 w-4 text-ink-2 group-hover:text-ink" aria-hidden="true" data-id="element-1864" data-mp="3rs9hmlwrvsmw" />
      </button>

      <p className="mt-6 max-w-[46ch] text-[17px] leading-relaxed text-ink-2" data-id="element-1865" data-mp="3nrzctckpv89m">
        We looked up <code className="rounded-md bg-sunken px-1.5 py-0.5 text-[15px] font-semibold text-ink" data-id="element-1866" data-mp="gczd1wbcqswr">{pathname}</code> in 60+ languages.
        No results — not even in Latin.
      </p>

      <div className="mt-9 flex flex-wrap justify-center gap-4" data-id="element-1867" data-mp="7j2wqy4vcap9">
        <BrutalButton to="/" data-id="element-1868" data-mp="blhefewsx4ey">Take me home</BrutalButton>
        <BrutalButton to="/app" variant="surface" data-id="element-1869" data-mp="bjwibkuu9dez">
          Back to my lessons
        </BrutalButton>
      </div>
    </section>;
}
function Globe() {
  return <motion.svg viewBox="0 0 48 48" className="h-[62%] w-[62%]" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" whileHover={{
    rotate: 25
  }} transition={spring.bouncy} data-id="element-1870" data-mp="2lb1bu50m6gb">
      <circle cx="24" cy="24" r="18" fill="rgb(var(--mint))" data-id="element-1871" data-mp="3ouc21fl3lb51" />
      <ellipse cx="24" cy="24" rx="8" ry="18" fill="rgb(var(--brand-tint))" data-id="element-1872" data-mp="1j0m3v1lkkxt3" />
      <path d="M6 24 H42 M24 6 V42 M9 15 C18 19 30 19 39 15 M9 33 C18 29 30 29 39 33" data-id="element-1873" data-mp="1n1eadn324olo" />
    </motion.svg>;
}